<template>
    <div class="editable-text-wrapper" @click="preventOnClick($event)" @dblclick.stop="handleDblClick($event)">
        <template v-if="!editMode">
            <div id="editable-label" class="editable-div user-select-none">
                <span v-if="label">{{ label }}</span>
                <span>
                    {{ text || defaultText }}
                </span>
                <c-button
                    v-if="renameBtn && allowEditing"
                    v-tippy="tooltipOptions"
                    class="rename-btn no-drag"
                    :content="tooltip"
                    size="sm"
                    @click.stop="enterTitleEditMode"
                >
                    <font-awesome-icon icon="fa-solid fa-pen" />
                </c-button>
            </div>
        </template>
        <input
            v-else
            ref="editableInputRef"
            v-model="inputValue"
            class="editable-input form-control form-control-sm"
            :type="type"
            @keydown="onTextInputKeyDown($event)"
            @blur="onFocusLost"
            @click="$event.stopImmediatePropagation()"
        />
    </div>
</template>

<script lang="ts" setup>
import { Ref, computed, nextTick, onBeforeMount, ref } from 'vue';
import CButton from '@/components/shared/CButton';

const emit = defineEmits<{
    (event: 'input', value: string | number): void;
}>();

const props = withDefaults(
    defineProps<{
        value: string | number;
        defaultText?: string;
        allowEditing?: boolean;
        tooltipDirection?: string;
        tooltip?: string;
        label?: string;
        type?: string;
        preventClick?: boolean;
        preventEmptyValue?: boolean;
        renameBtn?: boolean;
    }>(),
    {
        defaultText: '',
        allowEditing: true,
        tooltipDirection: 'top',
        tooltip: 'Rename',
        label: '',
        type: 'search',
        preventClick: false,
        renameBtn: true
    }
);

const editMode = ref(false);
const inputValue = ref('');

const editableInputRef: Ref<HTMLInputElement | null> = ref(null);

const tooltipOptions = computed(() => {
    const options: Record<string, unknown> = { placement: props.tooltipDirection, interactive: false };
    if (!props.tooltip.length) {
        options.trigger = '';
    }
    return options;
});

const text = computed({
    get(): string | number {
        return props.value;
    },
    set(value: string | number) {
        inputValue.value = value as string;
        emit('input', value);
    }
});

onBeforeMount(() => {
    inputValue.value = text.value as string;
});

const enterTitleEditMode = (event: MouseEvent) => {
    if (!props.allowEditing) return;
    /**
     * Add a small delay before entering edit mode so event listener that fire
     * due to click events have an opportunity to run.
     */
    event.stopPropagation();
    setTimeout(() => {
        editMode.value = true;
        inputValue.value = text.value as string;

        nextTick().then(() => {
            editableInputRef.value?.focus();
        });

        event.stopImmediatePropagation();
        event.preventDefault();
    }, 20);
};

const onFocusLost = () => {
    inputValue.value = props.preventEmptyValue && !inputValue.value?.length ? String(text.value) : inputValue.value;
    if (editMode.value && inputValue.value !== text.value) {
        text.value = inputValue.value;
    }
    editMode.value = false;
};

const onTextInputKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && editMode.value) {
        // Exit edit mode on enter and trigger the change event
        event.preventDefault();
        event.stopPropagation();

        onFocusLost();
    } else if (editMode.value && (event.key === 'Escape' || event.key === 'Esc')) {
        // Exit without triggering a change event
        event.preventDefault();
        event.stopPropagation();
        editMode.value = false;
    }
    event.stopImmediatePropagation();
};

const preventOnClick = (event: MouseEvent) => {
    if (props.preventClick) {
        event.stopImmediatePropagation();
    }
};

const handleDblClick = (event: MouseEvent) => {
    if (props.allowEditing) {
        enterTitleEditMode(event);
    }
};
</script>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.editable-text-wrapper {
    .rename-btn {
        visibility: hidden;
        margin-left: 0.25rem;
        color: get-color('gray-400');
        &:hover {
            color: initial;
        }
    }
    &:hover .rename-btn {
        visibility: initial;
    }
}
</style>
